/**
 * Quiz prompt templates and contextual prompt building.
 */
export const ENHANCED_PROMPT_TEMPLATES = {
    'true-false': `Create a true or false question about {subcategory} in {subject}. 
    CRITICAL FORMAT REQUIREMENTS:
    - Start with "True or False:" followed by a clear statement
    - The statement must be factual and testable
    - Provide the correct answer (True/False)
    - Example format: "True or False: [Statement]. Answer: [True/False]"
    
    Context: {subtopics}
    Difficulty: {difficulty}
    Subject: {subject}
    Subcategory: {subcategory}`,
    'multiple-choice': `Create a multiple-choice question about {subcategory} in {subject}.
    FORMAT REQUIREMENTS:
    - Clear question followed by 4 options (A, B, C, D)
    - Only one correct answer
    - Plausible distractors
    - For each incorrect option, name the idea a student mixed up (short phrase)
    - Put those phrases in distractor_misconceptions keyed by A/B/C/D; do not tag the correct option
    - Example format: "Question? A) Option1 B) Option2 C) Option3 D) Option4 Answer: [Letter]"
    
    Context: {subtopics}
    Difficulty: {difficulty}`,
    'short-answer': `Create a short-answer question about {subcategory} in {subject}.
    FORMAT REQUIREMENTS:
    - Direct question that can be answered in 1-3 words
    - Provide the correct answer
    - Example format: "Question? Answer: [Brief answer]"
    
    Context: {subtopics}
    Difficulty: {difficulty}`,
    'open-ended': `Create an open-ended question about {subcategory} in {subject}.
    FORMAT REQUIREMENTS:
    - Thought-provoking question requiring detailed response
    - Provide guidance for expected response
    - Example format: "Question? Guidance: [Expected response type]"
    
    Context: {subtopics}
    Difficulty: {difficulty}`,
    'scenario-based': `Create a scenario-based question about {subcategory} in {subject}.
    FORMAT REQUIREMENTS:
    - Present a realistic scenario
    - Ask what should be done or what happens next
    - Provide guidance for expected approach
    - Example format: "Scenario: [Situation] Question: [What to do?] Guidance: [Expected approach]"
    
    Context: {subtopics}
    Difficulty: {difficulty}`,
};
/**
 * Build contextual prompt with enhanced context integration.
 */
export const SKILL_TAGGING_REQUIREMENT = `SKILL TAGGING (required on every question):
- Include "skill_label": a short human-readable skill (e.g. "Adding fractions with unlike denominators").
- Include "skill_codes": an array with one canonical slug derived from that skill (lowercase, hyphenated, e.g. ["adding-fractions-unlike-denominators"]).
- Never use "__untagged", "untagged", or an empty skill. If the skill is unclear, pick the most specific subtopic from the prompt.`;
export const MISCONCEPTION_TAGGING_REQUIREMENT = `CONTENT MIX-UPS (idea tags, not process errors):
- For multiple-choice, include "distractor_misconceptions": an object mapping wrong-option letters to a short mix-up phrase (e.g. {"B": "Confusing voltage and current"}).
- Never tag the correct option. These are which idea was mixed up, not codes like mc_wrong_option.
- For short-answer or fill-in-the-gap, optionally include "misconceptions": an array of expected mix-up phrases.`;
export function buildContextualPrompt(params) {
    let contextualPrompt = '';
    if (params.subtopics.length > 0) {
        contextualPrompt += `PRIMARY CONTEXT - Focus on these specific subtopics:
      ${params.subtopics.map((topic, i) => `${i + 1}. ${topic}`).join('\n')}
      
      Each question must directly relate to one or more of these subtopics.`;
    }
    if (params.customPrompt) {
        contextualPrompt += `\n\nCUSTOM REQUIREMENTS:
      ${params.customPrompt}
      
      Ensure all questions align with these custom requirements.`;
    }
    if (params.lessonContent) {
        contextualPrompt += `\n\nLESSON CONTENT CONTEXT:
      ${params.lessonContent.substring(0, 1000)}...
      
      CONTEXT INTEGRATION REQUIREMENTS:
      - Extract key terms from the lesson content above
      - Incorporate at least 30% of key terms in questions/options
      - Use specific terminology from the lesson content
      - Test understanding of the specific lesson content provided
      - Avoid generic questions that could apply to any content
      - Focus on the specific concepts, examples, and details mentioned in the content`;
    }
    if (params.learningObjectives.length > 0) {
        contextualPrompt += `\n\nLEARNING OBJECTIVES:
      ${params.learningObjectives.map((obj, i) => `${i + 1}. ${obj}`).join('\n')}
      
      Questions should assess these specific learning outcomes.`;
    }
    if (params.rubric) {
        contextualPrompt += `\n\nSCORING RUBRIC GUIDANCE:
      Rubric Name: ${params.rubric.name}
      ${params.rubric.description ? `Description: ${params.rubric.description}` : ''}
      
      ${params.rubric.criteria && params.rubric.criteria.length > 0 ? `SCORING CRITERIA:
      ${params.rubric.criteria.map((criterion, i) => {
            const levels = criterion.rubric_levels || [];
            return `${i + 1}. ${criterion.description || 'Criterion ' + (i + 1)} (Max Points: ${criterion.max_points || 10})
        ${levels.length > 0 ? `   Scoring Levels:
        ${levels.map((level, j) => `   - ${level.description}: ${level.points} points`).join('\n')}` : ''}`;
        }).join('\n\n')}
      
      IMPORTANT: Generate questions that align with these scoring criteria. Ensure that:
      - Questions can be evaluated using the provided rubric criteria
      - Answer quality can be assessed against the rubric levels
      - Questions test the specific skills and knowledge outlined in the criteria
      - For open-ended and scenario-based questions, ensure answers can be scored using the rubric levels` : ''}`;
    }
    contextualPrompt += `\n\n${SKILL_TAGGING_REQUIREMENT}\n\n${MISCONCEPTION_TAGGING_REQUIREMENT}`;
    return contextualPrompt;
}
